import * as React from 'react';
import { ApiHelper, isApiHelperErrorResponse } from '../ApiHelper';
import { useLoaderData } from "react-router-dom";
import { useLocation, useNavigate } from 'react-router-dom';
import { DetailsList, DetailsListLayoutMode, IDetailsRowProps, DetailsRow } from '@fluentui/react/lib/DetailsList';
import DataTable from 'react-data-table-component';
import { Spinner, SpinnerSize } from '@fluentui/react/lib/Spinner';
import { Dropdown, DropdownMenuItemType, IDropdownOption, IDropdownStyles } from '@fluentui/react/lib/Dropdown';
import { ComboBox, IComboBox, IComboBoxOption } from '@fluentui/react/lib/ComboBox';
import { ChoiceGroup, IChoiceGroupOption } from '@fluentui/react/lib/ChoiceGroup';
import { DefaultButton } from '@fluentui/react';
import { Callout, DirectionalHint, Panel, PanelType, TextField, Spinner as FluentSpinner, MessageBar, MessageBarType, Stack } from '@fluentui/react';
import { Toggle } from '@fluentui/react/lib/Toggle';
import { useBoolean } from '@fluentui/react-hooks';
import { convertToCSV, downloadCSV } from '../utils/csvExport.ts';
import { Line } from 'react-chartjs-2';
import 'chartjs-adapter-date-fns';
import { CommitDetailPanelContent } from './GraphView.tsx';
import { MetricApiCpu, MetricApiDuration } from '../utils/constants';
import { useConfig } from '../configContext';
import ProfilerIcon from '../Profiler.ico';
import { ProfilePane } from './FlameGraph.tsx';
import type { CommitDetails } from '../interfaces/CommitDetails';
import { buildApiProfileOptions, filterApiProfileOptions, findApiOptionByText, getProfileSelectorParts, ConsumerSuffix, FunctionPrefix, type ApiProfileType } from './DailyApiProfiles.helpers';

type LoaderData = {
    apis: string[];
};

type UpdateState = (id: string, value: any) => void;

type ProfileSelectorProps = {
    apis: string[];
};

type ProfileSelectorMenuProps = {
    apis?: string[];
    apitype: ApiProfileType;
    api: string | null;
    farm: string | null;
    date: string | null;
    updateState: UpdateState;
    diffMode: boolean;
};

type ApiComboBoxProps = {
    apis: string[];
    placeholder: string;
    apitype: ApiProfileType;
    updateState: UpdateState;
    value?: string | null;
};

type DropdownSelectProps = {
    id: string;
    prefix: string;
    placeholder: string;
    size: number;
    updateState: UpdateState;
    value?: string | null;
};

type ProfileListProps = {
    path: string;
    updateState: UpdateState;
    showCompare?: boolean;
};

type TimelineListProps = {
    path: string;
};

type ProfileItemProps = {
    profile: any;
    updateState: UpdateState;
    showCompare?: boolean;
};

type ComparisonTableProps = {
    leftPath: string;
    rightPath: string;
    type: string;
    nav?: (update: Record<string, any>) => void;
};

type ApiProfileComparisonResponse = {
    methods: any[];
    diffSas?: string;
};

type CommitImpactResponse = {
    commits?: any[];
    processId?: number;
    environmentId?: number;
};

const ProfileTypes =
{
    TimeAndCpuRooted: { display: "CPU and waits, scoped", sortOrder: 1, single: false },
    OnCpuRooted: { display: "CPU, scoped", sortOrder: 2, single: true },
    TimeRooted: { display: "Time, scoped", sortOrder: 3, single: true },
    OffCpuRooted: { display: "Waits, scoped", sortOrder: 4, single: true },
    TimeAndCpu: { display: "CPU and waits", sortOrder: 5, single: false },
    OnCpu: { display: "CPU", sortOrder: 6, single: true },
    Time: { display: "Time", sortOrder: 7, single: true },
    OffCpu: { display: "Waits", sortOrder: 8, single: true },
    AdjacentOnCpu: { display: "AdjacentOnCpu", sortOrder: 9, single: true },
};

export { ProfileTypes };

export function ApiProfileList() {
    const { apis } = useLoaderData() as LoaderData;
    window.document.title = 'PR Guardian - Daily api profiles';

    return (
        <div style={{ margin: '20px'} }>
            <FAQButton/>
            <div>&nbsp;</div>
            <ProfileSelector
                apis={apis}
                />
        </div>
            );
}
export async function apiLoader() {
    const apiUrl = '/api/apiprofiles/list';
    const apis = await ApiHelper<string[]>(apiUrl);
    return { apis };
}

function ProfileSelector({ apis }: ProfileSelectorProps) {

    let location = useLocation();
    const navigate = useNavigate()
    let navState = location.state;
    if (navState == null) {
        const qs = new URLSearchParams(location.search);
        navState = {
            path: qs.get('path'),
            diffType: qs.get('difftype'),
            diffPath: qs.get('diffpath'),
            frameName: qs.get('frameName')
        };
    }
    const selectorParts = getProfileSelectorParts(navState.path);
    const stateApi = selectorParts.api;
    const statePath = navState.path;
    const [api, setApi] = React.useState(stateApi);
    const [farm, setFarm] = React.useState(selectorParts.farm);
    const [date, setDate] = React.useState(selectorParts.date);
    const [path, setPath] = React.useState(statePath);
    const [compare, setCompare] = React.useState(navState.diffType);
    const [apitype, setApiType] = React.useState<ApiProfileType>(selectorParts.apiType);

    const nav = (update) => {
        const targetState = { ...navState, ...update };
        const qs = new URLSearchParams();
        if (targetState.path != null) {
            qs.set('path', targetState.path);
        }

        if (targetState.diffType != null) {
            qs.set('difftype', targetState.diffType);
            qs.set('diffpath', targetState.diffPath);
        }

        // Preserve commit-impact params across navigations
        const currentQs = new URLSearchParams(location.search);
        for (const [key, value] of currentQs.entries()) {
            if (key.startsWith('ci')) qs.set(key, value);
        }
        // Add flamegraph param if present
        if (targetState.frameName != null) {
            qs.set('frameName', targetState.frameName);
        }

        const url = location.pathname + '?' + qs.toString();
        navigate(url, { state: targetState })
    }


    // let options = apis.map(a => ({ text: a }))
    const updateState = (id, value) => {
        switch (id) {
            case 'api':
                setApi(value);
                setFarm(null);
                setDate(null);
                setPath(null);
                setCompare(null);
                nav({ path: null, diffType: null, diffPath: null, frameName: null });
                break;
            case 'farm':
                setFarm(value);
                setDate(null);
                setPath(null);
                setCompare(null);
                nav?.({ frameName: null });
                break;
            case 'date':
                setDate(value);
                setPath(api + '/' + farm + '/' + value);
                setCompare(null);
                nav({ path: api + '/' + farm + '/' + value, diffType: null, diffPath: null, frameName: null })
                break;
            case 'apitype':
                setApiType(value);
                setApi(null);
                setFarm(null);
                setDate(null);
                setPath(null);
                setCompare(null);
                nav({ frameName: null });
                break;
            case 'compare':
                setCompare(value);
                nav({ frameName: null });
                break;

        }
    }
    return (
        <div>
            <h4>Select an api profile</h4>
            <ProfileSelectorMenu
                apis={apis}
                apitype={apitype}
                api={api}
                farm={farm}
                date={date}
                updateState={updateState}
                diffMode={false }
            />
            {
                path != null &&
                <ProfileList path={path} updateState={updateState} showCompare={true} />
            }
            {
                path != null && farm != null &&
                <CommitImpactSection
                    api={api}
                    farm={farm}
                    date={date}
                    compare={compare}
                    diffPath={navState.diffPath}
                />
            }
            {
                path != null && compare != null &&
                <div>
                        <ProfileComparison path={path} prefix={api + '/' + farm} diffpath={navState.diffPath} type={compare} nav={nav} />
                </div>
            }
            {
                path != null && !path.startsWith(FunctionPrefix) &&
                <div>
                    <TimelineList path={path} />
                </div>
            }
        </div>
    );
}

function ProfileSelectorMenu({ apis, apitype, api, farm, date, updateState, diffMode }: ProfileSelectorMenuProps) {

    const useSelect = 1
    return (
        <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 20 }}>
            {(!diffMode || api == null || !api.startsWith(FunctionPrefix)) && <div>
                <ApiTypeSelector
                    apitype={apitype}
                    updateState={updateState}
                    diffMode={diffMode}
                />
            </div>}
            {!diffMode && <div>
                <ApiComboBox
                    apis={apis}
                    placeholder="Choose or type an api..."
                    apitype={apitype}
                    updateState={updateState}
                    value={api}
                />
            </div>}
            {
                api != null &&
                <div>
                    {!useSelect && <DropdownSelect
                    id="farm"
                    prefix={api}
                    placeholder="Choose a farm..."
                    size={20}
                    updateState={updateState}
                    />}
                    {useSelect && <DropdownSelect
                        id="farm"
                        prefix={api}
                        placeholder="Choose a farm..."
                        size={20}
                        updateState={updateState}
                        value={farm}
                    />}
                </div>
            }
            {
                farm != null &&
                <div>
                    {!useSelect && <DropdownSelect
                        id="date"
                        prefix={api + '/' + farm}
                        placeholder="Choose a date..."
                        size={20}
                        updateState={updateState}
                    />}
                    {useSelect && <DropdownSelect
                        id="date"
                        prefix={api + '/' + farm}
                        placeholder="Choose a date..."
                        size={20}
                        updateState={updateState}
                        value={date}
                    />}
                </div>
            }
        </div>

    )
}
function ApiComboBox({ apis, placeholder, apitype, updateState, value }: ApiComboBoxProps) {
    const options = React.useMemo(() => buildApiProfileOptions(apis, apitype), [apis, apitype]);
    const [text, setText] = React.useState('');
    const selectedOption = React.useMemo(
        () => options.find(option => option.key === value) ?? null,
        [options, value],
    );
    const filteredOptions = React.useMemo(() => filterApiProfileOptions(text, options), [options, text]);

    React.useEffect(() => {
        setText(selectedOption?.text ?? '');
    }, [selectedOption?.text]);

    const commitSelection = React.useCallback((selection: string | null) => {
        if (selection != null) {
            updateState('api', selection);
        }
    }, [updateState]);

    const handleChange = React.useCallback((event: React.FormEvent<IComboBox>, option?: IComboBoxOption) => {
        if (option?.key != null) {
            const selected = String(option.key);
            setText(option.text ?? selected);
            commitSelection(selected);
        }
    }, [commitSelection]);

    const handleInputValueChange = React.useCallback((newValue?: string) => {
        setText(newValue ?? '');
    }, []);

    const handleBlur = React.useCallback(() => {
        const resolvedOption = findApiOptionByText(text, options);
        if (resolvedOption != null) {
            setText(resolvedOption.text);
            if (resolvedOption.key !== value) {
                commitSelection(resolvedOption.key);
            }
            return;
        }

        setText(selectedOption?.text ?? '');
    }, [commitSelection, options, selectedOption?.text, text, value]);

    return (
        <ComboBox
            selectedKey={value ?? undefined}
            text={text}
            placeholder={options.length > 0 ? placeholder : 'Loading...'}
            options={filteredOptions}
            allowFreeform={true}
            autoComplete="on"
            onChange={handleChange}
            onInputValueChange={handleInputValueChange}
            onBlur={handleBlur}
            styles={{ root: { width: 640, maxWidth: 'calc(100vw - 40px)' } }}
        />
    );
}

function DropdownSelect({ id, prefix, placeholder, size, updateState, value }: DropdownSelectProps) {
    const [values, setValues] = React.useState<string[]>([]);
    const [selectedItem, setSelectedItem] = React.useState<IDropdownOption | undefined>(undefined);


    let options: IDropdownOption[] = [];

    React.useEffect(() => {
        ApiHelper<string[]>('/api/apiprofiles/list?path=' + prefix)
            .then(data =>
                setValues(data)
            )
    }, [prefix])

    options = values.map((v) => { return { key: v, text: v } });

    // Resolve the option key that matches 'value'.
    // For date fields, value is a clean date "2024-02-12" but the option key
    // may include a sample count suffix "2024-02-12 [1234]".
    let resolvedKey: string | null = null;
    if (value != null) {
        if (id === 'date') {
            const match = values.find(v => v === value || v.startsWith(value + ' ['));
            resolvedKey = match ?? null;
        } else {
            resolvedKey = value;
        }
    }

    const onChange = (event: React.FormEvent<HTMLDivElement>, item: IDropdownOption): void => {
        setSelectedItem(item);

        // Extract date portion if it contains sample count
        // Format: "2024-02-12 [1234]" -> "2024-02-12"
        let value = item.key as string;
        if (id === 'date' && value.includes('[')) {
            value = value.split(' [')[0].trim();
        }

        updateState(id, value);
    };


    return (
        <span>
            <Dropdown
                selectedKey={selectedItem ? selectedItem.key : undefined}
                // eslint-disable-next-line react/jsx-no-bind
                onChange={onChange}
                placeholder={values.length > 0 ? placeholder : "Loading..."}
                options={options}
                defaultSelectedKey={resolvedKey}
                />
        </span>
    )
}

function ApiTypeSelector({ apitype, updateState, diffMode }) {
    const [selectedKey, setSelectedKey] = React.useState<string | undefined>(apitype.toString());

    const onChange = React.useCallback((ev: React.SyntheticEvent<HTMLElement>, option: IChoiceGroupOption) => {
        setSelectedKey(option.key);
        updateState('apitype', parseInt(option.key));
    }, []);

    let options = [
        { key: "0", text: "Commercial" },
        { key: "1", text: "Consumer" }
    ];

    if (!diffMode) {
        options.push({key:"2", text:"Methods"})
    }
    return <ChoiceGroup selectedKey={selectedKey} options={options} onChange={onChange}/>;
}

function ProfileList({ path, updateState, showCompare = false }: ProfileListProps) {
    const [profiles, setProfiles] = React.useState<any[] | null>(null);


    React.useEffect(() => {
        ApiHelper<any[]>('/api/apiprofiles/get?path=' + path)
            .then(data =>
                setProfiles(data)
            )
    }, [path])

    if (profiles != null) {
        profiles.sort((a, b) => { return ProfileTypes[a.type].sortOrder - ProfileTypes[b.type].sortOrder });
    }
    return (
        <div>
            {profiles != null && profiles.length > 0 &&
                <div>
                    <br/>
                    <div>Data is based on {profiles[0].requestCount} request{profiles[0].requestCount > 1 ? 's' : ''}</div>
                    <div>{profiles[0].onCpu / 1000} ms spent on cpu. {profiles[0].waits / 1000} ms spent waiting.</div>
                    <div><i>VPN connection required to open in azure profile viewer</i></div>
                    {path.startsWith(FunctionPrefix) &&
                        <div>Rooted profiles are scoped to the method and do not show the calling stacks.</div>
                    }
                </div>
            }
            {profiles != null && profiles.length > 0 &&
                <table cellPadding={10} style={{ width: 'auto' }}>
                    <tbody>
                        {profiles.map(p => (<ProfileItem profile={p} updateState={updateState} showCompare={showCompare} />))}
                    </tbody>
                </table>
            }
        </div>
    )
}

export { ProfileList };

const _timelineColumns = [
    {
        key: 'correlationId', name: 'CorrelationId', fieldName: 'correlationId',
        onRender: (item) => { return (<a href={'/RequestTimeline?path=' + item.path} title="View request timeline" target="_blank">{item.correlationId}</a>); }
        , minWidth: 100, maxWidth: 200, isResizable: true
    },
    { key: 'duration', name: 'Duration', fieldName: 'duration', minWidth: 100, maxWidth: 200, isResizable: true },
    { key: 'cpuDuration', name: 'CPU', fieldName: 'cpuDuration', minWidth: 100, maxWidth: 200, isResizable: true },
    { key: 'date', name: 'Time', fieldName: 'date', minWidth: 100, maxWidth: 200, isResizable: true },
];

export { _timelineColumns };

function TimelineList({ path }: TimelineListProps) {
    const [timelines, setTimelines] = React.useState<any[] | null>(null);

    React.useEffect(() => {
        ApiHelper<any[]>('/api/apitimelines/list?path=' + path)
            .then(data =>
                setTimelines(data)
            )
    }, [path])

    return (
        <div>
            {timelines != null &&
                <div>
                    <br />
                    <br />
                    <h4>Sample request timelines</h4>
                    <div>Click on CorrelationId to view the timeline</div>

                    <DetailsList
                        compact={true}
                        items={timelines}
                        columns={_timelineColumns}
                        //    .filter(column => { return auditorView || column.key !== 'authorName' })}
                        //onRenderRow={_onRenderRow}
                        //setKey="set"
                        layoutMode={DetailsListLayoutMode.fixedColumns}
                        selectionMode={0}
                        //selection={this._selection}
                        //selectionPreservedOnEmptyClick={true}
                        //isSelectedOnFocus={false}
                    //    ariaLabelForSelectionColumn="Toggle selection"
                    //    ariaLabelForSelectAllCheckbox="Toggle selection for all items"
                    //    checkButtonAriaLabel="select row"
                        />
                </div>
            }
        </div>
    )
}

export { TimelineList };

function ProfileItem({ profile, updateState, showCompare = false }: ProfileItemProps) {
    const onLinkClick = () => {
        updateState('compare', profile.type);
    }
    return (<tr>
        <td><b>{ProfileTypes[profile.type].display}</b></td>
        <td><a href={profile.sasUrl} target="_blank" rel="noopener noreferrer">Download</a></td>
        <td><a href={'https://azprofilerclko.azurewebsites.net/ClickOnce/AzureProfilerViewer.application?Blob=' + profile.base64SasUrl + '&NewPipeline=true'} target="_blank" rel="noopener noreferrer" title="Open in Azure Profiler Viewer. Requires VPN.">Open in viewer</a></td>
        {showCompare && <td><a href="javascript:;" title="Compare with another date" onClick={onLinkClick}>Compare</a></td>}
    </tr>)
}

export { ProfileItem };

function ProfileComparison({ path, prefix, type, nav, diffpath }) {
    const parts = (diffpath ?? path).split('/');
    const [apiType, setApiType] = React.useState<ApiProfileType>(parts[0].startsWith(FunctionPrefix) ? 2 : (parts[0].endsWith(ConsumerSuffix) ? 1 : 0));
    const [api, setApi] = React.useState(parts[0]);
    const [farm, setFarm] = React.useState(parts[1]);
    const [date, setDate] = React.useState((diffpath != null && parts.length > 2) ? parts[2] : null);
    //const [diffPath, setDiffPath] = React.useState();
    const updateState = (id, value) => {
        switch (id) {
            case 'farm':
                setFarm(value);
                setDate(null);
                break;
            case 'date':
                setDate(value);
                nav(
                    {
                        diffType: type,
                        diffPath: api + "/" + farm + "/" + value
                    }
                );
                break;
            case 'apitype':
                {
                    switch (value) {
                        case 0:
                            if (api.endsWith(ConsumerSuffix)) {
                                setApi(api.substring(0, api.length - ConsumerSuffix.length));
                            }
                            if (api.startsWith(FunctionPrefix)) {
                                setApi(api.substring(FunctionPrefix.length));
                            }
                            break;
                        case 1:
                            if (!api.endsWith(ConsumerSuffix)) {
                                setApi(api + ConsumerSuffix);
                            }
                            if (api.startsWith(FunctionPrefix)) {
                                setApi(api.substring(FunctionPrefix.length) + ConsumerSuffix);
                            }
                            break;
                        case 2:
                            if (!api.startsWith(FunctionPrefix)) {
                                setApi(FunctionPrefix + api.replace(new RegExp(`${ConsumerSuffix}$`), ''));
                            }
                            break;
                    }
                }
                setApiType(value);
                setFarm(null);
                setDate(null);
                break;
        }
    }

    return (
        <div>
            {/*<ComboBox*/}
            {/*    id="comparedate"*/}
            {/*    prefix={prefix}*/}
            {/*    placeholder="Choose a date to compare to..."*/}
            {/*    size={40}*/}
            {/*    apitype={0}*/}
            {/*    updateState={updateState}*/}
            {/*/>*/}
            <br/>
            <h5>Select a profile to compare to:</h5>
            <ProfileSelectorMenu
                apitype={apiType}
                api={api}
                farm={farm}
                date={date}
                updateState={updateState}
                diffMode={true}
                />
            <br />
            {
                date &&
                <ComparisonTable
                    leftPath={api + "/" + farm + "/" + date}
                    rightPath={path}
                    type={type}
                    nav={nav}
                />
            }
        </div>
    )
}

const _diffColumns: Array<{ id?: string; timeOnly?: boolean; [key: string]: any }> = [
    { name: 'Class', selector: row => row.parentName, sortable: true, id: 'parentName', wrap:false },
    { name: 'Method', selector: row => row.name, sortable: true, id: 'name', wrap: true, minWidth:'50em'},
    { name: 'Internal', selector: row => row.isInternal ? 'yes' : 'no', sortable: true, id: 'internal', width:'fit-content'},
    { name: 'Cpu(left)', selector: row => row.exclusive, sortable: true, id: 'cpuleft', right:true, timeOnly:true, maxWidth:'20' },
    { name: 'Cpu(right)', selector: row => row.exclusiveRight, sortable: true, id: 'cpuright', right: true, timeOnly: true },
    { name: 'Cpu(delta)', selector: row => row.exclusiveRight - row.exclusive, sortable: true, id: 'cpudelta', right: true, timeOnly: true },
    { name: 'Waits(left)', selector: row => row.total - row.exclusive, sortable: true, id: 'waitsleft', right: true, timeOnly: true },
    { name: 'Waits(right)', selector: row => row.totalRight - row.exclusiveRight, sortable: true, id: 'waitsright', right: true, timeOnly: true, width: '10em' },
    { name: 'Waits(delta)', selector: row => row.totalRight - row.exclusiveRight - (row.total - row.exclusive), sortable: true, id: 'waitsdelta', right: true, timeOnly: true, width: '10em' },
    { name: 'Total(left)', selector: row => row.total, sortable: true, id: 'totalleft', right: true, maxWidth: '10em' },
    { name: 'Total(right)', selector: row => row.totalRight, sortable: true, id: 'totalright', right: true, width: '10em' },
    { name: 'Total(delta)', selector: row => row.delta, sortable: true, id: 'delta', right: true, maxWidth: '10em', minWidth:'30px' },
];

function ComparisonTable({ leftPath, rightPath, type, nav }: ComparisonTableProps) {
    const [data, setData] = React.useState<ApiProfileComparisonResponse | null>(null);
    const [filterExternal, setFilterExternal] = React.useState(true);
    const [profileModalData, setProfileModalData] = React.useState(null);

    const onFilterChange = (e) => {
        setFilterExternal(e.target.checked)
    }

    const handleProfilerClick = (row) => {
        // Get stream name from profile type
        const streamName = `${type}.stream`;

        // Combine parent + name for full frame identifier
        const frameName = row.parentName + '!' + row.name;

        // Determine metric ID from stream name
        const metric = streamName.includes('OnCpu') ? 6 : 7;

        setProfileModalData({
            mode: 'apiprofile',
            leftPath: leftPath,
            rightPath: rightPath,
            streamName: streamName,
            frameName: frameName,
            metric: metric,
            onclose: () => {
                setProfileModalData(null);
                // Clear URL param when closing
                nav({ frameName: null });
            }
        });

        // Update URL with flamegraph param (streamName derived from existing difftype)
        nav?.({ frameName: frameName });
    };

    // Define columns inside component to access handlers
    const columnsWithIcon: Array<{ id?: string; timeOnly?: boolean; [key: string]: any }> = [
        {
            name: '',
            width: '40px',
            cell: (row) => (
                <img
                    src={ProfilerIcon}
                    alt="View flamegraph"
                    style={{ cursor: 'pointer', width: '16px', height: '16px', objectFit: 'contain' }}
                    onClick={(e) => {
                        e.stopPropagation();
                        handleProfilerClick(row);
                    }}
                    title="View flamegraph for this method"
                />
            )
        },
        ..._diffColumns
    ];

    // Auto-open flamegraph if URL contains frameName
    React.useEffect(() => {
        const qs = new URLSearchParams(window.location.search);
        const urlFrameName = qs.get('frameName');

        // Only auto-open if:
        // 1. URL has frameName param
        // 2. Modal is not already open
        // 3. Data is loaded
        if (urlFrameName && !profileModalData && data) {
            // Find matching row in data
            const matchingRow = data.methods.find(method => {
                const frameName = method.parentName + '!' + method.name;
                return frameName === urlFrameName;
            });

            if (matchingRow) {
                // Derive streamName from type (which comes from difftype param)
                const streamName = `${type}.stream`;
                const metric = streamName.includes('OnCpu') ? 6 : 7;

                setProfileModalData({
                    mode: 'apiprofile',
                    leftPath: leftPath,
                    rightPath: rightPath,
                    streamName: streamName,
                    frameName: urlFrameName,
                    metric: metric,
                    onclose: () => {
                        setProfileModalData(null);
                        nav({ frameName: null });
                    }
                });
            }
        }
    }, [data, profileModalData, leftPath, rightPath, type, nav]);

    // Close flamegraph when URL params are removed (browser back/forward)
    const location = useLocation();
    React.useEffect(() => {
        const qs = new URLSearchParams(location.search);
        const urlFrameName = qs.get('frameName');

        // If modal is open but URL doesn't have params, close it
        if (profileModalData && !urlFrameName) {
            setProfileModalData(null);
        }
    }, [location.search, profileModalData]);

    let url = '/api/apiprofiles/compare?left=' + leftPath + '&right=' + rightPath + '&profiletype=' + type;
    React.useEffect(() => {
        ApiHelper<ApiProfileComparisonResponse>(url)
            .then(data =>
                setData(data)
            )
    }, [leftPath, rightPath, type])

    var cols = columnsWithIcon.filter(x => {
        if ((type.startsWith('OnCpu') || type.startsWith('OffCpu')) && x.timeOnly) return false;
        if (filterExternal && x.id == 'internal') return false;
        return true;
    });;

    var dataToDisplay = null;
    if (data != null) {
        dataToDisplay = data.methods;
        if (filterExternal) {
            dataToDisplay = dataToDisplay.filter(x => x.isInternal);
        }
    }

    const handleDownloadCSV = () => {
        if (!data?.methods) return;

        // Apply same filtering as displayed data
        const filteredMethods = filterExternal
            ? data.methods.filter(x => x.isInternal)
            : data.methods;

        // Convert EntityCostTracker objects to CSV rows
        const csvRows = filteredMethods.map((method) => ({
            'Class': method.parentName || '',
            'Method': method.name || '',
            'Internal': method.isInternal ? 'YES' : 'NO',
            'Cpu(left)': method.exclusive,
            'Cpu(right)': method.exclusiveRight,
            'Cpu(delta)': method.exclusiveRight - method.exclusive,
            'Waits(left)': method.total - method.exclusive,
            'Waits(right)': method.totalRight - method.exclusiveRight,
            'Waits(delta)': (method.totalRight - method.exclusiveRight) - (method.total - method.exclusive),
            'Total(left)': method.total,
            'Total(right)': method.totalRight,
            'Total(delta)': method.delta
        }));

        // Convert to CSV format
        const csv = convertToCSV({ rows: csvRows });

        // Generate filename with timestamp
        const timestamp = new Date().toISOString().replace(/[:.]/g, '-').slice(0, -5);
        const filename = `profile_comparison_${timestamp}.csv`;

        // Trigger download
        downloadCSV(csv, filename);
    };

    var profilerLink = null;
    if (data != null) {
        profilerLink = 'https://azprofilerclko.azurewebsites.net/ClickOnce/AzureProfilerViewer.application?Diff=' + data.diffSas + '&AbsoluteDelta=True&NewPipeline=true'
    }

    let leftId = '';
    let rightId = '';
    let leftParts = leftPath.split('/');
    let rightParts = rightPath.split('/');

    if (leftParts[0] != rightParts[0]) {
        if (leftParts[0].endsWith(ConsumerSuffix)) {
            leftId = 'Consumer'; rightId = 'Commercial';
        } else {
            leftId = 'Commercial'; rightId = 'Consumer';
        }
    }
    for (var i = 1; i < 3; i++)
    if (leftParts[1] != rightParts[1]) {
        leftId += (leftId.length > 0 ? '/' : '') + leftParts[i];
        rightId += (rightId.length > 0 ? '/' : '') + rightParts[i];
    }

   return (
       <div>
           {
               data == null &&
               <Spinner style={{ visibility: data == null ? 'visible' : 'hidden' }} size={SpinnerSize.large} />

           }
           {data &&
               <div>
                   <br />
                   <p>Durations in the table below are in microseconds. Left={leftId}. Right={rightId}. <a href={profilerLink} target="_blank" rel="noopener noreferrer" >Open diff in Azure Profiler Viewer</a>
                   </p>
                   <p>
                       <input type="checkbox" id="filterExternal" checked={filterExternal} onChange={onFilterChange} /><label htmlFor="filterExternal"> Show internal code only</label>&nbsp;&nbsp;&nbsp;&nbsp;
                       <DefaultButton
                           text="Download as CSV"
                           iconProps={{ iconName: 'Download' }}
                           onClick={handleDownloadCSV}
                           disabled={!data?.methods || data.methods.length === 0}
                       />
                   </p>
                   <DataTable
                   data={dataToDisplay}
                   columns={cols}
                   striped={true}
                   defaultSortFieldId={'Total'}
                   defaultSortAsc={false}
                   highlightOnHover={true}
                   dense={true}
                   pagination
                   paginationPerPage={50}
                       />
               </div>
           }

           {profileModalData && (
               <ProfilePane {...profileModalData} />
           )}

        </div>
   )
}

// ─── Interesting Commits Section ──────────────────────────────────────────────

const SUPPORTED_FARMS = ['SPDF', 'MSIT'];


function CommitImpactSection({ api, farm, date, compare, diffPath }) {
    const location  = useLocation();
    const navigate  = useNavigate();
    const thresholdRef = React.useRef(null);
    const [isThresholdOpen, { toggle: toggleThreshold, setFalse: closeThreshold }] = useBoolean(false);
    const config = useConfig();
    const service = config?.services?.find((s: any) => s.id === 0) ?? null;

    // Read / write CI params from URL directly
    const getQs = () => new URLSearchParams(location.search);
    const setCiParam = (key, value) => {
        const qs = getQs();
        if (value === null || value === undefined) qs.delete(key);
        else qs.set(key, String(value));
        navigate(location.pathname + '?' + qs.toString(), { replace: true, state: location.state });
    };

    const defaultMinIncrease = api?.startsWith('Func-') ? 100 : 1000;
    const raw = getQs();
    const ciMetric       = raw.get('ciMetric')       != null ? Number(raw.get('ciMetric'))       : MetricApiCpu;
    const ciLookback     = raw.get('ciLookback')      != null ? Number(raw.get('ciLookback'))      : 14;
    const ciMinThreshold = raw.get('ciMinThreshold')  != null ? Number(raw.get('ciMinThreshold'))  : null;
    const ciMinIncrease  = raw.get('ciMinIncrease')   != null ? Number(raw.get('ciMinIncrease'))   : defaultMinIncrease;
    const ciView         = raw.get('ciView')          || 'graph';
    const ciXScope       = raw.get('ciXScope')        || 'window';
    const ciYMode        = raw.get('ciYMode')         || 'absolute';
    const ciPage         = raw.get('ciPage')          != null ? Number(raw.get('ciPage'))          : 0;

    const isCompareMode = !!compare;
    const [expanded,             setExpanded]             = React.useState(isCompareMode);
    const [loading,              setLoading]              = React.useState(false);
    const [data,                 setData]                 = React.useState<any[] | null>(null);
    const [error,                setError]                = React.useState<string | null>(null);
    const [resolvedProcessId,    setResolvedProcessId]    = React.useState<number>(0);
    const [resolvedEnvironmentId,setResolvedEnvironmentId]= React.useState<number>(0);
    const [panelCommit,          setPanelCommit]          = React.useState<any | null>(null);
    const [panelDetails,         setPanelDetails]         = React.useState<any | null>(null);
    const [panelLoading,         setPanelLoading]         = React.useState<boolean>(false);
    const [panelError,           setPanelError]           = React.useState<string | null>(null);

    // Derive date range
    const getRangeDates = () => {
        if (isCompareMode && date && diffPath) {
            const d1 = new Date(date);
            const d2 = new Date(diffPath.split('/')[2] || date);
            return { startDate: new Date(Math.min(d1.getTime(), d2.getTime())), endDate: new Date(Math.max(d1.getTime(), d2.getTime())) };
        }
        if (date) {
            const end   = new Date(date);
            const start = new Date(date);
            start.setDate(start.getDate() - ciLookback);
            return { startDate: start, endDate: end };
        }
        return null;
    };

    const openPanel = React.useCallback(async (c: any) => {
        setPanelCommit(c);
        setPanelDetails(null);
        setPanelLoading(true);
        setPanelError(null);
        try {
            const result = await ApiHelper<CommitDetails, { error?: string }>(
                `/api/service/${service?.name}/commits/${c.commitMetaData.commitId}/details`,
                undefined, {}, true
            );
            if (isApiHelperErrorResponse<{ error?: string }>(result)) {
                setPanelError(result.data?.error ?? 'Failed to load commit details.');
            } else {
                setPanelDetails({
                    ...result,
                    commitId: c.commitMetaData.commitId
                });
            }
        } catch {
            setPanelError('Failed to load commit details.');
        } finally {
            setPanelLoading(false);
        }
    }, [service]);

    const fetchData = React.useCallback(async () => {
        const range = getRangeDates();
        if (!range) return;
        setLoading(true);
        setError(null);
        setData(null);
        setCiParam('ciPage', 0);

        const params = new URLSearchParams({
            path:       `${api}/${farm}`,
            startDate:  range.startDate.toISOString(),
            endDate:    range.endDate.toISOString(),
            metricType: String(ciMetric),
        });
        if (ciMinThreshold != null) params.set('minThreshold', String(ciMinThreshold));
        if (ciMinIncrease  != null) params.set('minIncrease',  String(ciMinIncrease));

        try {
            const result = await ApiHelper<CommitImpactResponse, { error?: string }>(`/api/apiprofiles/commits?${params}`, undefined, {}, true);
            if (isApiHelperErrorResponse<{ error?: string }>(result)) {
                setError(result.data?.error ?? 'Request failed.');
            } else {
                setData(result.commits ?? []);
                setResolvedProcessId(result.processId ?? 0);
                setResolvedEnvironmentId(result.environmentId ?? 0);
            }
        } catch {
            setError('Failed to load commit impact data.');
        } finally {
            setLoading(false);
        }
    }, [api, farm, date, diffPath, ciMetric, ciLookback, ciMinThreshold, ciMinIncrease, isCompareMode]);

    // Auto-fetch in compare mode when section mounts / deps change
    React.useEffect(() => {
        if (isCompareMode && expanded) {
            fetchData();
        }
    }, [isCompareMode, fetchData]);

    // Not supported for this farm
    if (!SUPPORTED_FARMS.includes(farm)) {
        return (
            <div style={{ marginTop: '24px', color: '#888', fontSize: '13px' }}>
                Commit impact analysis is not available for farm <strong>{farm}</strong>.
            </div>
        );
    }

    const thresholdLabel = (() => {
        const parts = [];
        if (ciMinThreshold != null) parts.push(`> ${ciMinThreshold}`);
        if (ciMinIncrease  != null) parts.push(`Δ > ${ciMinIncrease}`);
        return parts.length ? parts.join(' | ') : 'Threshold';
    })();

    const range = getRangeDates();
    const totalPages = data && data.length > 0 ? Math.ceil(data.length / 10) : 1;

    const hasData = data != null && data.length > 0 && !loading;

    return (
        <div style={{ marginTop: '28px', borderTop: '1px solid #EDEBE9', paddingTop: '16px' }}>
            {/* Section header */}
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '10px', cursor: 'pointer' }}
                 onClick={() => setExpanded(e => !e)}>
                <span style={{ fontSize: '16px', fontWeight: 600 }}>Interesting Commits</span>
                <span style={{ color: '#888', fontSize: '13px' }}>{expanded ? '▲' : '▼'}</span>
            </div>

            {expanded && (
                <>
                    {/* Controls bar — single row:
                        [Metric][Lookback][Threshold][Find]   gap   [< N of M >][Tabular][Graph]   auto   [X: …][Y: …]
                    */}
                    <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '10px', flexWrap: 'nowrap' }}>

                        {/* Group 1: fetch filters (always visible) */}
                        <select className="filter-dropdown"
                            value={ciMetric}
                            onChange={e => setCiParam('ciMetric', e.target.value)}>
                            <option value={MetricApiCpu}>Api µs cpu</option>
                            <option value={MetricApiDuration}>Api µs</option>
                        </select>
                        {!isCompareMode && (
                            <select className="filter-dropdown"
                                value={ciLookback}
                                onChange={e => setCiParam('ciLookback', e.target.value)}>
                                <option value={7}>7 days</option>
                                <option value={14}>14 days</option>
                                <option value={30}>30 days</option>
                                <option value={60}>60 days</option>
                            </select>
                        )}
                        <div ref={thresholdRef} style={{ display: 'inline-block' }}>
                            <button className="topbutton" onClick={e => { e.stopPropagation(); toggleThreshold(); }}>
                                {thresholdLabel}
                            </button>
                            {isThresholdOpen && (
                                <Callout
                                    target={thresholdRef.current}
                                    onDismiss={closeThreshold}
                                    directionalHint={DirectionalHint.bottomLeftEdge}
                                    setInitialFocus>
                                    <div style={{ padding: '16px', minWidth: '340px' }}>
                                        <CiThresholdRow
                                            label="Peak value exceeds"
                                            description="Only show commits whose highest sampled metric value exceeds this."
                                            value={ciMinThreshold}
                                            onChange={v => setCiParam('ciMinThreshold', v)}
                                        />
                                        <CiThresholdRow
                                            label="Change exceeds"
                                            description="Only show commits where the metric increased by more than this amount."
                                            value={ciMinIncrease}
                                            onChange={v => setCiParam('ciMinIncrease', v)}
                                        />
                                    </div>
                                </Callout>
                            )}
                        </div>
                        {!isCompareMode && (
                            <button className="topbutton" onClick={fetchData}>Find Commits</button>
                        )}

                        {/* Group 2: pagination + view toggle (only when data is loaded) */}
                        {hasData && (
                            <>
                                <span style={{ marginLeft: '16px' }} />
                                <button className="topbutton" disabled={ciPage === 0}
                                    onClick={() => setCiParam('ciPage', ciPage - 1)}>&lt;</button>
                                <span style={{ fontSize: '12px', color: '#444', whiteSpace: 'nowrap' }}>
                                    Page {ciPage + 1} of {totalPages}
                                </span>
                                <button className="topbutton" disabled={ciPage >= totalPages - 1}
                                    onClick={() => setCiParam('ciPage', ciPage + 1)}>&gt;</button>
                                <button className={`topbutton${ciView === 'table' ? ' active' : ''}`}
                                    onClick={() => setCiParam('ciView', 'table')}>Tabular</button>
                                <button className={`topbutton${ciView === 'graph' ? ' active' : ''}`}
                                    onClick={() => setCiParam('ciView', 'graph')}>Graph</button>
                            </>
                        )}

                        {/* Group 3: X/Y axis controls — far right, graph view only */}
                        {hasData && ciView === 'graph' && (
                            <>
                                <span style={{ marginLeft: 'auto' }} />
                                <span style={{ fontSize: '12px', color: '#666', alignSelf: 'center' }}>X:</span>
                                <button className={`topbutton${ciXScope === 'window' ? ' active' : ''}`}
                                    onClick={() => setCiParam('ciXScope', 'window')}>Window</button>
                                <button className={`topbutton${ciXScope === 'all' ? ' active' : ''}`}
                                    onClick={() => setCiParam('ciXScope', 'all')}>All data</button>
                                <span style={{ fontSize: '12px', color: '#666', alignSelf: 'center', marginLeft: '8px' }}>Y:</span>
                                <button className={`topbutton${ciYMode === 'absolute' ? ' active' : ''}`}
                                    onClick={() => setCiParam('ciYMode', 'absolute')}>Absolute</button>
                                <button className={`topbutton${ciYMode === 'relative' ? ' active' : ''}`}
                                    onClick={() => setCiParam('ciYMode', 'relative')}>Relative</button>
                            </>
                        )}
                    </div>

                    {/* Status / error */}
                    {loading && <Spinner size={SpinnerSize.small} label="Loading commits..." labelPosition="right" />}
                    {error   && <div style={{ color: '#c00', fontSize: '13px', marginBottom: '8px' }}>{error}</div>}

                    {/* Results */}
                    {data != null && !loading && (
                        <>
                            {data.length === 0 && (
                                <div style={{ color: '#666', fontSize: '13px' }}>No commits found for the selected criteria.</div>
                            )}
                            {data.length > 0 && (
                                <>
                                    {ciView === 'table' && (
                                        <CommitImpactTable
                                            commits={data}
                                            ciPage={ciPage}
                                            setPanelCommit={openPanel}
                                        />
                                    )}
                                    {ciView === 'graph' && (
                                        <CommitImpactGraph
                                            commits={data}
                                            startDate={range?.startDate}
                                            endDate={range?.endDate}
                                            ciXScope={ciXScope}
                                            ciYMode={ciYMode}
                                            setCiParam={setCiParam}
                                            ciPage={ciPage}
                                            setPanelCommit={openPanel}
                                            ciMetric={ciMetric}
                                        />
                                    )}
                                </>
                            )}
                        </>
                    )}

                    {/* Detail panel */}
                    {panelCommit && service && (
                        <Panel isLightDismiss isOpen type={PanelType.medium}
                            onDismiss={() => { setPanelCommit(null); setPanelDetails(null); }}
                            closeButtonAriaLabel="Close"
                            headerText={panelCommit.commitMetaData.title}>
                            {panelLoading && <Spinner size={SpinnerSize.medium} label="Loading commit details..." labelPosition="right" />}
                            {panelError && <div style={{ color: '#c00', fontSize: '13px', padding: '8px' }}>{panelError}</div>}
                            {panelDetails && (
                                <CommitDetailPanelContent
                                    service={service}
                                    commit={panelDetails}
                                    process={resolvedProcessId}
                                    env={resolvedEnvironmentId}
                                    metric={ciMetric}
                                />
                            )}
                        </Panel>
                    )}
                </>
            )}
        </div>
    );
}

function CiThresholdRow({ label, description, value, onChange }) {
    return (
        <div style={{ marginBottom: '14px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '3px' }}>
                <span style={{ fontWeight: 600, fontSize: '13px', flex: 1 }}>{label}</span>
                <TextField
                    type="number"
                    defaultValue={value?.toString() ?? ''}
                    key={value ?? 'empty'}
                    onBlur={(e: any) => onChange(e.target.value === '' ? null : Number(e.target.value))}
                    onKeyDown={(e: any) => { if (e.key === 'Enter') (e.target as any).blur(); }}
                    styles={{
                        root: { width: '90px' },
                        fieldGroup: { height: '28px', minHeight: '28px' }
                    }}
                />
                {value != null
                    ? <button style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '0 2px', fontSize: '15px', color: '#666' }}
                              onClick={() => onChange(null)} title="Clear">×</button>
                    : <span style={{ width: '19px' }} />
                }
            </div>
            <div style={{ fontSize: '12px', color: '#666' }}>{description}</div>
        </div>
    );
}

function CommitImpactTable({ commits, ciPage, setPanelCommit }) {
    const [sortCol, setSortCol] = React.useState<string | null>(null);
    const [sortDir, setSortDir] = React.useState<'asc' | 'desc'>('asc');

    const handleSort = (col: string) => {
        if (sortCol === col) {
            if (sortDir === 'asc') setSortDir('desc');
            else { setSortCol(null); setSortDir('asc'); }
        } else {
            setSortCol(col);
            setSortDir('asc');
        }
    };

    const sortIndicator = (col: string) => sortCol === col ? (sortDir === 'asc' ? ' ↑' : ' ↓') : '';

    const sortedCommits = React.useMemo(() => {
        if (!sortCol) return commits;
        return [...commits].sort((a, b) => {
            let aVal: any, bVal: any;
            switch (sortCol) {
                case 'author':   aVal = a.commitMetaData.authorName  ?? ''; bVal = b.commitMetaData.authorName  ?? ''; break;
                case 'merged':   aVal = a.commitMetaData.pRMergeDate ?? ''; bVal = b.commitMetaData.pRMergeDate ?? ''; break;
                case 'maxValue': aVal = a.maxValue;   bVal = b.maxValue;   break;
                case 'increase': aVal = a.increase;   bVal = b.increase;   break;
                case 'status':   aVal = a.isRegression ? 1 : 0; bVal = b.isRegression ? 1 : 0; break;
                default: return 0;
            }
            if (aVal < bVal) return sortDir === 'asc' ? -1 : 1;
            if (aVal > bVal) return sortDir === 'asc' ?  1 : -1;
            return 0;
        });
    }, [commits, sortCol, sortDir]);

    const rows = sortedCommits.slice(ciPage * 10, (ciPage + 1) * 10);

    const thStyle: React.CSSProperties = { padding: '6px 10px', cursor: 'pointer', userSelect: 'none' };

    return (
        <table style={{ borderCollapse: 'collapse', fontSize: '13px', width: '100%' }}>
            <thead>
                <tr style={{ borderBottom: '1px solid #EDEBE9', textAlign: 'left' }}>
                    <th style={{ padding: '6px 10px' }}>Commit</th>
                    <th style={thStyle} onClick={() => handleSort('author')}>Author{sortIndicator('author')}</th>
                    <th style={thStyle} onClick={() => handleSort('merged')}>Merged{sortIndicator('merged')}</th>
                    <th style={{ ...thStyle, textAlign: 'right' }} onClick={() => handleSort('maxValue')}>Max value{sortIndicator('maxValue')}</th>
                    <th style={{ ...thStyle, textAlign: 'right' }} onClick={() => handleSort('increase')}>Increase{sortIndicator('increase')}</th>
                    <th style={thStyle} onClick={() => handleSort('status')}>Status{sortIndicator('status')}</th>
                </tr>
            </thead>
            <tbody>
                {rows.map(c => {
                    const md = c.commitMetaData;
                    return (
                        <tr key={md.commitId}
                            style={{ borderBottom: '1px solid #F3F2F1', cursor: 'pointer',
                                     backgroundColor: c.isRegression ? 'rgba(220,38,38,0.05)' : undefined }}
                            onClick={() => setPanelCommit(c)}>
                            <td style={{ padding: '6px 10px' }}>
                                <button
                                    style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer',
                                             color: '#0078d4', textDecoration: 'underline', textAlign: 'left', fontSize: 'inherit' }}
                                    onClick={e => { e.stopPropagation(); setPanelCommit(c); }}
                                    title={md.title}>
                                    {(md.title || md.commitId || '').substring(0, 60)}{(md.title || '').length > 60 ? '…' : ''}
                                </button>
                            </td>
                            <td style={{ padding: '6px 10px' }}>{md.authorName}</td>
                            <td style={{ padding: '6px 10px', whiteSpace: 'nowrap' }}>
                                {md.prMergeDate ? new Date(md.prMergeDate).toLocaleDateString() : '—'}
                            </td>
                            <td style={{ padding: '6px 10px', textAlign: 'right' }}>{c.maxValue.toFixed(0)}</td>
                            <td style={{ padding: '6px 10px', textAlign: 'right' }}>{c.increase.toFixed(0)}</td>
                            <td style={{ padding: '6px 10px' }}>
                                {c.isRegression && (
                                    <span style={{ background: '#FDE7E9', color: '#A4262C', borderRadius: '3px', padding: '1px 6px', fontSize: '11px', marginRight: '4px' }}>
                                        Regression
                                    </span>
                                )}
                            </td>
                        </tr>
                    );
                })}
            </tbody>
        </table>
    );
}

function CommitImpactGraph({ commits, startDate, endDate, ciXScope, ciYMode, setCiParam, ciPage, setPanelCommit, ciMetric }) {
    const displayed = commits.slice(ciPage * 10, (ciPage + 1) * 10);
    const chartRef = React.useRef<any>(null);
    const [soloDatasetIndex, setSoloDatasetIndex] = React.useState<number | null>(null);

    // Build chart datasets
    const REGRESSION_COLOR = 'rgba(220,38,38,0.85)';
    const NORMAL_COLORS    = ['#0078d4','#107c10','#797673','#8764b8','#038387','#ca5010','#004e8c','#498205'];

    let colorIdx = 0;
    const datasets = displayed.map(c => {
        const md    = c.commitMetaData;
        const label = md.title || md.commitId || '';
        const pts   = (c.timeSeries || [])
            .filter(p => {
                const date = new Date(p.profileDate);
                const day = date.getDay();
                return day !== 0 && day !== 6; // Exclude Sunday (0) and Saturday (6)
            })
            .map(p => ({
                x: new Date(p.profileDate),
                y: p.value
            }));

        let values = pts;
        if (ciYMode === 'relative' && pts.length > 0) {
            const baseline = pts[0].y;
            values = pts.map(p => ({ x: p.x, y: p.y - baseline }));
        }

        const color = c.isRegression ? REGRESSION_COLOR : NORMAL_COLORS[colorIdx++ % NORMAL_COLORS.length];
        return { label, data: values, borderColor: color, backgroundColor: color, pointStyle: false as const,
                 borderWidth: c.isRegression ? 2 : 1.5, tension: 0 };
    });

    // Reset solo state when displayed commits change (pagination)
    React.useEffect(() => {
        setSoloDatasetIndex(null);
    }, [displayed]);

    // X axis bounds
    const xMin = ciXScope === 'window' && startDate ? startDate : undefined;
    const xMax = ciXScope === 'window' && endDate   ? endDate   : undefined;

    const options: any = {
        responsive: true,
        animation: false,
        scales: {
            x: { type: 'time', time: { unit: 'day' }, min: xMin, max: xMax,
                 title: { display: true, text: 'Date' } },
            y: { title: { display: true, text: ciYMode === 'relative' ? 'Change (µs)' : 'µs' } }
        },
        plugins: {
            legend: {
                position: 'bottom' as const,
                labels: { boxWidth: 12, font: { size: 11 } },
                onClick: (evt: any, legendItem: any, legend: any) => {
                    const index = legendItem.datasetIndex;
                    const chart = legend.chart;

                    if (soloDatasetIndex === index) {
                        // Second click on same item - show all
                        chart.data.datasets.forEach((_: any, i: number) => {
                            chart.setDatasetVisibility(i, true);
                        });
                        setSoloDatasetIndex(null);
                    } else {
                        // First click or different item - solo this one
                        chart.data.datasets.forEach((_: any, i: number) => {
                            chart.setDatasetVisibility(i, i === index);
                        });
                        setSoloDatasetIndex(index);
                    }
                    chart.update();
                }
            },
            tooltip: {
                enabled: true,
                mode: 'nearest' as const,
                intersect: false,
                callbacks: {
                    title: (items: any[]) => {
                        if (!items.length) return '';
                        const ds = datasets[items[0].datasetIndex];
                        return ds?.label ?? '';
                    },
                    label: (item: any) => `${item.parsed.y.toFixed(0)} µs`
                }
            }
        },
        onClick: (_evt: any, elements: any[], chart: any) => {
            // Direct hit on a data point, or fall back to what the tooltip is showing
            const target = elements.length > 0
                ? elements[0]
                : chart.tooltip?.getActiveElements()?.[0];
            if (target) {
                const commit = displayed[target.datasetIndex];
                if (commit) setPanelCommit(commit);
            }
        },
        onHover: (_evt: any, _elements: any[], chart: any) => {
            const active = (chart.tooltip?.getActiveElements()?.length ?? 0) > 0;
            chart.canvas.style.cursor = active ? 'pointer' : 'default';
        }
    };

    return (
        <Line ref={chartRef} data={{ datasets }} options={options} />
    );
}

// ─────────────────────────────────────────────────────────────────────────────

function FAQButton() {
    const [isOpen, { setTrue: openPanel, setFalse: dismissPanel }] = useBoolean(false);
    const navigate = useNavigate()

    return (
        <>
            <DefaultButton
                id="legendButton"
                onClick={openPanel}
                text="Frequently asked questions"
            />
            {isOpen && (
                <Panel
                    isLightDismiss
                    isOpen={isOpen}
                    type={PanelType.extraLarge}
                    onDismiss={dismissPanel}
                    closeButtonAriaLabel="Close"
                >
                    <RenderFAQ/>
                </Panel>
            )}
            &nbsp;&nbsp;&nbsp;&nbsp;
            <DefaultButton
                id="adhocprofiler"
                onClick={() => navigate('/adhocapiprofiler')}
                text="Adhoc api profiler"
            />
        </>
    );
}

function RenderFAQ() {
    return (
        <div>
            <h4>What is the source of the data?</h4>
            <p>The data is generated from 3 sources:
                <ul>
                    <li>Raw machine-level Azure Profiler traces, which contain cpu samples every millisecond and context switch events.</li>
                    <li>RequestUsage logs that tell us which requests were running on the machine at the time it was profiled.</li>
                    <li>ULS logs which tell us which threads those requests were running on.</li>
                </ul>
                With all that information we can transform AzureProfiler hit count into actual elapsed thread time, and see samples of what a request was doing and when.
            </p>
            <h4>What are some limitations of the data?</h4>
            <p>
                <ul>
                    <li>The data is thread time, not wall clock time. If two threads run in parallel and take 50 ms each, 100 ms will be reported.</li>
                    <li>It depends on a ULS message being logged on the thread. If a task has no logging it may not be included.</li>
                    <li>Waits may be double-counted: if you start a task and synchronously wait for its result, the waits in the task will be counted twice.</li>
                    <li>Profiler traces are 30-second long, so requests taking longer than that will never be included.</li>
                </ul>
            </p>
            <h4>The api I am interested in is not in the list</h4>
            <p>
                We profile 1% of the requests for 30 second for each farm. So an api needs to have at least 4 RPS with errorType = 'Success' to have a chance to be represented.
            </p>
            <h4>Can I get data for other farms, or with different criteria for the select requests?</h4>
            <p>
                The list of farms is limited at this time. To get data on different farms or different criteria, please start a workflow on Diagnostics Hub from your SAW and your Torus account.
                Use this <a href="https://odspdiagnosticshub.microsoft.com/New/?workflow=Operation%20cpu%20and%20timeline">link</a>. Note that this is not available for farms behind EUDB boundary.
            </p>
            <h4>Can I get details for a method that is not in the list?</h4>
            <p>
                If the method averages more than 1 millisecond, contact pmoul@microsoft.com to get it added to the list.
            </p>
            <h4>I have other feedback</h4>
            <p>Please contact pmoul@microsoft.com.</p>
        </div>
    )

}
